/**
 * Image Workflow Types - Dual Layer Structure
 *
 * Config Layer: Pure configuration, portable templates
 * Canvas Layer: UI state, editor-specific
 *
 * Key changes from v1:
 * - Node `key` replaces `id` (user-readable, e.g., "start", "prompt_1")
 * - Edge uses `from/to` instead of `id/source/target`
 * - Positions separated to canvas layer
 * - Null fields omitted
 */

import type {
  FieldDefinition,
  OutputFieldDefinition,
  InputFieldMapping,
} from "./workflowFields";

// ============================================================================
// Enums
// ============================================================================

export type PromptStrategy = "agent" | "raw_llm";
export type StorageMode = "temp" | "library";
export type ExecutionStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "partial"
  | "cancelled";
export type StepStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "skipped";
export type NodeType =
  | "start"
  | "prompt"
  | "generate"
  | "edit"
  | "fuse"
  | "lucky_charm"
  | "push"
  | "output";
export type PromptInputMode = "manual" | "agent" | "llm";
export type LuckyCharmInputMode = "manual" | "agent" | "llm";
export type PushTargetType =
  | "follow_start"
  | "current_user"
  | "all_users"
  | "specific_users";

// ============================================================================
// Variable System
// ============================================================================

export interface BuiltInVariables {
  user_id: string;
  user_name: string;
  user_email?: string;
  execution_id: string;
  workflow_id: string;
  workflow_name: string;
  timestamp: string;
  date: string;
  time: string;
}

export interface CustomVariable {
  key: string;
  value: string;
  description?: string;
}

// ============================================================================
// Node Data (Config only, no null fields)
// ============================================================================

export interface NodeData {
  label?: string;

  // Start Node
  variables?: CustomVariable[];
  enabled_builtins?: (keyof BuiltInVariables)[];

  // Prompt Node
  input_mode?: PromptInputMode;
  user_input?: string;
  agent_type?: string;
  output_schema_key?: string; // Output schema for Agent mode
  llm_model?: string;
  llm_temperature?: number;
  system_prompt?: string; // LLM mode: custom system prompt
  prompt?: string;

  // Image Nodes (generate/edit/fuse)
  model?: string;
  aspect_ratio?: string;
  size?: string;
  quality?: string;
  web_search?: boolean;

  // Output Node
  save_to_library?: boolean;
  filename?: string;
  category?: string;
  artifact_type?: string; // Artifact type (lucky_charm, soulmate, etc.)
  artifact_title?: string; // Title for the artifact
  artifact_description?: string; // Description for the artifact

  // Lucky Charm Node
  lucky_charm_input_mode?: LuckyCharmInputMode; // 'manual' | 'agent' | 'llm'
  lucky_charm_phrase?: string; // Manual mode: direct phrase input
  lucky_charm_extra_prompt?: string; // LLM mode: extra prompt for generation
  lucky_charm_agent_type?: string; // Agent mode: agent type
  lucky_charm_llm_model?: string; // LLM mode: model ID
  lucky_charm_llm_temperature?: number; // LLM mode: temperature
  // Lucky Charm customization options (shared across all modes)
  background_image_id?: string;
  background_set_id?: string;
  font_id?: number;
  chart_id?: string; // Optional: use specific chart for context
  emotion_trend?: string; // Emotion trend for generation

  // Push Node
  push_target_type?: PushTargetType; // 'follow_start' | 'current_user' | 'all_users' | 'specific_users'
  push_target_user_ids?: string[]; // User IDs for specific_users mode
  push_sound?: string; // Notification sound (default: "default")
  push_badge?: number; // Badge number for iOS
  push_data?: Record<string, unknown>; // Additional data to send with notification
  push_mode?: "quick" | "template"; // default 'quick'
  push_action?: string; // quick mode: tap action (from PUSH_ACTION_MAP)
  push_template_name?: string; // template mode: template.name

  // Field Configuration (Base Node Architecture)
  input_fields?: FieldDefinition[]; // Custom input field definitions
  output_fields?: OutputFieldDefinition[]; // Custom output field definitions
  input_mappings?: InputFieldMapping[]; // Explicit input-to-output mappings

  // Index signature for React Flow
  [key: string]: unknown;
}

// ============================================================================
// Config Layer - Pure Configuration (Portable Template)
// ============================================================================

/**
 * Config Node - uses `key` instead of `id`
 * key: User-readable identifier, e.g., "start", "prompt_1", "generate_avatar"
 */
export interface ConfigNode {
  key: string;
  type: NodeType;
  data: NodeData;
}

/**
 * Config Edge - minimal, just connection
 */
export interface ConfigEdge {
  from: string; // source node key
  to: string; // target node key
}

/**
 * Workflow Config - Pure configuration, no UI state
 */
export interface WorkflowConfig {
  nodes: ConfigNode[];
  edges: ConfigEdge[];
}

// ============================================================================
// Canvas Layer - UI State (Editor-specific)
// ============================================================================

export interface NodePosition {
  x: number;
  y: number;
}

export interface WorkflowViewport {
  x: number;
  y: number;
  zoom: number;
}

/**
 * Canvas State - positions and viewport
 */
export interface CanvasState {
  positions: Record<string, NodePosition>; // key -> position
  viewport: WorkflowViewport;
}

// ============================================================================
// Combined Definition (stored in database)
// ============================================================================

export interface WorkflowDefinition {
  config: WorkflowConfig;
  canvas: CanvasState;
  // Legacy fields for backwards compatibility with old data
  nodes?: ConfigNode[]; // Old format (deprecated)
  edges?: ConfigEdge[]; // Old format (deprecated)
}

// ============================================================================
// Workflow Settings
// ============================================================================

export interface PromptConfig {
  agent_type?: string;
  model?: string;
  temperature?: number;
  system_prompt?: string;
}

export interface WorkflowSettings {
  prompt_strategy: PromptStrategy;
  prompt_config?: PromptConfig;
  storage_mode: StorageMode;
}

// ============================================================================
// Workflow CRUD
// ============================================================================

export interface WorkflowCreate {
  name: string;
  key: string; // Required: unique key for API reference (e.g., 'soulmate', 'lucky_charm')
  description?: string;
  definition: WorkflowDefinition;
  default_storage_mode?: StorageMode;
  prompt_strategy?: PromptStrategy;
  prompt_config?: PromptConfig;
}

export interface WorkflowUpdate {
  name?: string;
  key?: string; // Optional: unique key for API reference
  description?: string;
  definition?: WorkflowDefinition;
  default_storage_mode?: StorageMode;
  prompt_strategy?: PromptStrategy;
  prompt_config?: PromptConfig;
  is_active?: boolean;
  is_system_template?: boolean; // If true, workflow is accessible to all users
}

export interface WorkflowValidationWarning {
  rule: string;
  node_key: string;
  message: string;
}

export interface Workflow {
  id: string;
  key: string; // Unique key for API reference (e.g., 'soulmate', 'lucky_charm')
  name: string;
  description?: string;
  user_id?: string;
  definition: WorkflowDefinition;
  version: number;
  default_storage_mode: StorageMode;
  prompt_strategy: PromptStrategy;
  prompt_config: PromptConfig;
  execution_count: number;
  is_active: boolean;
  is_system_template: boolean; // True if workflow is accessible to all users (user_id is null)
  created_at: string;
  updated_at: string;
  warnings?: WorkflowValidationWarning[];
}

export interface WorkflowListResponse {
  total: number;
  workflows: Workflow[];
}

// ============================================================================
// Runtime Options (for workflow execution)
// ============================================================================

/**
 * Runtime options to override agent behavior during workflow execution.
 * Used to prevent workflow execution from affecting user's real memory.
 */
export interface RuntimeOptions {
  /** If true, memory toolkit won't be added to agent tools */
  disable_memory_tool?: boolean;
  /** If true, conversation won't be saved to memory after chat */
  disable_memory_write?: boolean;
  /** Hook stages to disable (e.g., 'FINAL') */
  disabled_hook_stages?: string[];
}

/**
 * User filter options for selecting valid users in batch execution.
 * All filters are combined with AND logic (all conditions must be met).
 */
export interface UserFilterOptions {
  /** 排除测试用户（user_id 以 'test_' 开头的用户）*/
  exclude_test?: boolean;
  /** 最低角色等级过滤：'free', 'premium', 'vip', 'pro' */
  min_role_level?: "free" | "premium" | "vip" | "pro";
  /** 仅包含最近 N 天内活跃的用户（基于 updated_at）*/
  active_days?: number;
  /** 仅包含有 self 星盘的用户（user_birth_charts.is_self = true）*/
  has_self_birth_chart?: boolean;
  /** 仅包含有活跃推送 token 的用户（push_tokens.is_active = true）*/
  has_push_token?: boolean;
}

// ============================================================================
// Workflow Execution
// ============================================================================

export interface ExecuteWorkflowRequest {
  workflow_id: string;
  /** Target type: current_user, all_valid_users, or specific_users */
  target_type?: "current_user" | "all_valid_users" | "specific_users";
  /** User IDs when target_type is specific_users */
  target_user_ids?: string[];
  /** User filter options (only used when target_type is 'all_valid_users') */
  user_filter?: UserFilterOptions;
  /** Runtime options to override agent behavior (e.g., disable memory) */
  runtime_options?: RuntimeOptions;
}

export interface ExecutionStep {
  id: string;
  node_key: string; // Changed from node_id to node_key
  node_type: string;
  step_order: number;
  status: StepStatus;
  input_data: Record<string, unknown>;
  output_data: Record<string, unknown>;
  task_id?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  error_message?: string;
}

export interface UserRun {
  id: string;
  execution_id: string;
  target_user_id: string;
  status: ExecutionStatus;
  total_steps: number;
  completed_steps: number;
  final_outputs: FileOutput[];
  created_at: string;
  started_at?: string;
  completed_at?: string;
  total_duration_ms?: number;
  error_message?: string;
  error_node_key?: string; // Changed from error_node_id
  steps?: ExecutionStep[];
}

export interface WorkflowExecution {
  id: string;
  workflow_id?: string;
  workflow_snapshot: {
    definition: WorkflowDefinition;
    prompt_strategy: PromptStrategy;
    prompt_config: PromptConfig;
    storage_mode: StorageMode;
    version: number;
  };
  status: ExecutionStatus;
  target_user_ids: string[];
  total_users: number;
  completed_users: number;
  failed_users: number;
  initiated_by: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  total_duration_ms?: number;
  error_summary?: string;
  user_runs?: UserRun[];
}

export interface ExecutionListResponse {
  total: number;
  executions: WorkflowExecution[];
}

export interface ExecutionStartResponse {
  execution_id: string;
  status: ExecutionStatus;
  total_users: number;
  message: string;
}

// ============================================================================
// Workflow History
// ============================================================================

export interface WorkflowHistory {
  id: string;
  workflow_id: string;
  version: number;
  definition: WorkflowDefinition;
  prompt_strategy: PromptStrategy;
  prompt_config: PromptConfig;
  changed_by?: string;
  change_reason?: string;
  created_at: string;
}

export interface WorkflowHistoryListResponse {
  total: number;
  history: WorkflowHistory[];
}

// ============================================================================
// Output / File References
// ============================================================================

export interface FileOutput {
  file_id: string;
  url: string;
  node_key: string; // Changed from node_id
  category?: string;
  saved_to_library: boolean;
}

export interface FileUrlResponse {
  file_id: string;
  url: string;
  expires_at?: string;
}

// ============================================================================
// React Flow Integration Types
// ============================================================================

import type { Node, Edge } from "@xyflow/react";

/**
 * React Flow node (runtime) - uses generated id internally
 */
export type WorkflowReactFlowNode = Node<
  NodeData & { nodeKey: string },
  NodeType
>;

/**
 * React Flow edge (runtime)
 */
export type WorkflowReactFlowEdge = Edge;

/**
 * Editor state for React Flow
 */
export interface WorkflowEditorState {
  nodes: WorkflowReactFlowNode[];
  edges: WorkflowReactFlowEdge[];
  viewport: WorkflowViewport;
}

// ============================================================================
// Conversion Utilities
// ============================================================================

/**
 * Generate React Flow ID from node key (for internal use)
 */
export function keyToReactFlowId(key: string): string {
  return `rf-${key}`;
}

/**
 * Extract node key from React Flow ID
 */
export function reactFlowIdToKey(id: string): string {
  return id.startsWith("rf-") ? id.slice(3) : id;
}

/**
 * Convert API definition to React Flow state
 * Supports both new dual-layer structure and legacy flat structure
 */
export function definitionToReactFlow(
  definition: WorkflowDefinition,
): WorkflowEditorState {
  // Support both new structure (config/canvas) and legacy structure (nodes/edges at root)
  const config = definition.config || {
    nodes: definition.nodes || [],
    edges: definition.edges || [],
  };
  const canvas = definition.canvas || {
    positions: {},
    viewport: { x: 0, y: 0, zoom: 1 },
  };

  // Handle legacy node format (id instead of key)
  const nodes = config.nodes.map(
    (node: ConfigNode & { id?: string; position?: NodePosition }) => {
      const key = node.key || node.id || "unknown";
      const position = canvas.positions[key] || node.position || { x: 0, y: 0 };
      return {
        id: keyToReactFlowId(key),
        type: node.type,
        position,
        data: { ...node.data, nodeKey: key },
      };
    },
  );

  // Handle legacy edge format (source/target instead of from/to, and from_ for Pydantic alias)
  const edges = config.edges.map(
    (
      edge: ConfigEdge & {
        id?: string;
        source?: string;
        target?: string;
        from_?: string;
      },
      index: number,
    ) => {
      const from = edge.from || edge.from_ || edge.source || "";
      const to = edge.to || edge.target || "";
      return {
        id: edge.id || `edge-${index}-${from}-${to}`,
        source: keyToReactFlowId(from),
        target: keyToReactFlowId(to),
      };
    },
  );

  return {
    nodes,
    edges,
    viewport: canvas.viewport,
  };
}

/**
 * Convert React Flow state to API definition
 */
export function reactFlowToDefinition(
  state: WorkflowEditorState,
): WorkflowDefinition {
  const positions: Record<string, NodePosition> = {};

  const nodes: ConfigNode[] = state.nodes.map((node) => {
    const key = node.data.nodeKey || reactFlowIdToKey(node.id);
    positions[key] = node.position;

    // Extract only config data, remove runtime fields
    const { nodeKey, _runtime, ...configData } = node.data as NodeData & {
      nodeKey?: string;
      _runtime?: unknown;
    };

    // Remove undefined/null values
    const cleanData = Object.fromEntries(
      Object.entries(configData).filter(
        ([, v]) => v !== undefined && v !== null,
      ),
    ) as NodeData;

    return {
      key,
      type: node.type as NodeType,
      data: cleanData,
    };
  });

  const edges: ConfigEdge[] = state.edges.map((edge) => ({
    from: reactFlowIdToKey(edge.source),
    to: reactFlowIdToKey(edge.target),
  }));

  return {
    config: { nodes, edges },
    canvas: { positions, viewport: state.viewport },
  };
}

/**
 * Create empty workflow definition
 */
export function createEmptyDefinition(): WorkflowDefinition {
  return {
    config: { nodes: [], edges: [] },
    canvas: { positions: {}, viewport: { x: 0, y: 0, zoom: 1 } },
  };
}

/**
 * Generate unique node key
 */
export function generateNodeKey(
  type: NodeType,
  existingKeys: string[],
): string {
  if (type === "start" && !existingKeys.includes("start")) {
    return "start";
  }

  let counter = 1;
  let key = type === "start" ? `start_${counter}` : type;

  while (existingKeys.includes(key)) {
    counter++;
    key = `${type}_${counter}`;
  }

  return key;
}
